import { Chip as MuiChip } from '@mui/material';
import { chipClasses, ChipProps } from '@mui/material/Chip';
import { styled } from '@mui/material/styles';

import { tokens } from '../../theme/styles/themeMui';

export type { ChipProps };

export const Chip: React.ComponentType<ChipProps> = styled(MuiChip)<ChipProps>(
  ({ theme, size, color }) => ({
    display: 'inline-flex',
    borderRadius: '100px',
    alignItems: 'center',
    flexShrink: 0,
    ...(size === 'medium' && {
      padding: '0px 16px',
      height: '36px',
      gap: '8px',
      [`.${chipClasses.label}`]: {
        ...tokens.typography.BodySRegular,
        padding: 0,
        color: 'inherit',
      },
      [`.${chipClasses.icon}`]: {
        margin: 0,
        width: '20px',
        height: '20px',
      },
      [`.${chipClasses.deleteIcon}`]: {
        margin: 0,
        fontSize: '20px',
        color: 'inherit',
      },
      [`&:has(.${chipClasses.icon})`]: {
        paddingLeft: '12px',
      },
      [`&:has(.${chipClasses.deleteIcon})`]: {
        paddingRight: '12px',
      },
    }),
    ...(size === 'small' && {
      padding: '4px 12px',
      gap: '8px',
      height: 'auto',
      [`.${chipClasses.label}`]: {
        ...tokens.typography.BodySRegular,
        padding: 0,
        color: 'inherit',
      },
      [`.${chipClasses.icon}`]: {
        margin: 0,
        width: '16px',
        height: '16px',
      },
      [`.${chipClasses.deleteIcon}`]: {
        margin: 0,
        fontSize: '16px',
        color: 'inherit',
      },
      [`&:has(.${chipClasses.icon})`]: {
        paddingLeft: '8px',
      },
      [`&:has(.${chipClasses.deleteIcon})`]: {
        paddingRight: '8px',
      },
    }),
    ...(size === 'xsmall' && {
      padding: '4px 8px',
      gap: '6px',
      height: 'auto',
      [`.${chipClasses.label}`]: {
        ...tokens.typography.BodyXsRegular,
        padding: 0,
        color: 'inherit',
      },
      [`.${chipClasses.icon}`]: {
        margin: 0,
        width: '16px',
        height: '16px',
      },
      [`.${chipClasses.deleteIcon}`]: {
        margin: 0,
        fontSize: '16px',
        color: 'inherit',
      },
      [`&:has(.${chipClasses.icon})`]: {
        paddingLeft: '6px',
      },
      [`&:has(.${chipClasses.deleteIcon})`]: {
        paddingRight: '7px',
      },
    }),
    ...(color === 'neutral' && {
      color: theme.palette.colors.content['neutral-primary'],
      backgroundColor: theme.palette.colors.bg['neutral-secondary'],
      border: `2px solid ${theme.palette.colors.border['neutral-secondary']}`,
      [`&:hover`]: {
        backgroundColor: theme.palette.colors['bg-active']['neutral-secondary'],
      },
      [`&:active`]: {
        backgroundColor: theme.palette.colors.bg['neutral-primary'],
        borderColor: theme.palette.colors['bg-active']['neutral-primary'],
        color: theme.palette.colors.content['primary-inverted'],
      },
    }),

    ...(color === 'danger' && {
      color: theme.palette.colors.content['danger-primary'],
      backgroundColor: theme.palette.colors.bg['danger-secondary'],
      border: `2px solid ${theme.palette.colors.border['danger-secondary']}`,
      [`&:hover`]: {
        backgroundColor: theme.palette.colors['bg-active']['danger-secondary'],
      },
      [`&:active`]: {
        backgroundColor: theme.palette.colors['bg-active']['danger-primary'],
        borderColor: theme.palette.colors['bg-active']['danger-primary'],
        color: theme.palette.colors.content['primary-inverted'],
      },
    }),
    ...(color === 'action' && {
      color: theme.palette.colors.content['action-primary'],
      backgroundColor: theme.palette.colors.bg['action-secondary'],
      border: `2px solid ${theme.palette.colors.border['action-secondary']}`,
      [`&:hover`]: {
        backgroundColor: theme.palette.colors['bg-active']['action-secondary'],
      },
      [`&:active`]: {
        backgroundColor: theme.palette.colors['bg-active']['action-primary'],
        borderColor: theme.palette.colors['bg-active']['action-primary'],
        color: theme.palette.colors.content['primary-inverted'],
      },
    }),
    ...(color === 'promo' && {
      color: theme.palette.colors.content['promo-primary'],
      backgroundColor: theme.palette.colors.bg['promo-secondary'],
      border: `2px solid ${theme.palette.colors.border['promo-secondary']}`,
      [`&:hover`]: {
        backgroundColor: theme.palette.colors['bg-active']['promo-secondary'],
      },
      [`&:active`]: {
        backgroundColor: theme.palette.colors['bg-active']['promo-primary'],
        borderColor: theme.palette.colors['bg-active']['promo-primary'],
        color: theme.palette.colors.content['primary-inverted'],
      },
    }),

    [`&.${chipClasses.disabled}`]: {
      color: theme.palette.colors.content.tertiary,
      backgroundColor: theme.palette.colors.bg['neutral-secondary'],
      border: `2px solid ${theme.palette.colors.border['neutral-secondary']}`,
    },
  }),
);
